/**
 * Map Firebase Auth error codes to user-friendly messages and whether to show
 * the global "connection error" banner (e.g. auth/network-request-failed).
 * @param {Error|{ code?: string, message?: string }} err - Firebase auth error
 * @param {(key: string) => string} t - i18n function
 * @returns {{ message: string, showConnectionBanner: boolean }}
 */
export function getAuthErrorMessage(err, t) {
  const code = err?.code ?? ''
  const fallback = err?.message || (typeof t === 'function' ? t('auth.logInFailed') : 'Something went wrong.')

  // Network/connection failures – show global banner and friendly message
  if (
    code === 'auth/network-request-failed' ||
    code === 'auth/connection-unavailable' ||
    (typeof err?.message === 'string' && err.message.includes('network'))
  ) {
    return {
      message: typeof t === 'function' ? t('connectionError.message') : 'Connection problem. Please check your internet and refresh the page.',
      showConnectionBanner: true,
    }
  }

  // Rate limit
  if (code === 'auth/too-many-requests') {
    return {
      message: typeof t === 'function' ? t('connectionError.tooManyRequests') : 'Too many attempts. Please try again later.',
      showConnectionBanner: false,
    }
  }

  // Known auth codes with fixed copy
  const known = {
    'auth/unauthorized-domain':
      'This domain is not authorized for sign-in. Try from localhost or your deployed domain.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled.',
    'auth/account-exists-with-different-credential':
      'This email is already registered with email and password. Please sign in with your password instead.',
    'auth/email-already-in-use': typeof t === 'function' ? t('auth.emailAlreadyInUse') : 'This email is already in use.',
  }

  if (known[code]) {
    return { message: known[code], showConnectionBanner: false }
  }

  return { message: fallback, showConnectionBanner: false }
}

/** Check if an error is an auth network/connection error (for unhandledrejection). */
export function isAuthNetworkError(err) {
  const code = err?.code ?? ''
  const msg = typeof err?.message === 'string' ? err.message : ''
  return (
    code === 'auth/network-request-failed' ||
    code === 'auth/connection-unavailable' ||
    msg.includes('auth/network-request-failed') ||
    msg.includes('network-request-failed')
  )
}
